import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {BookOfShadowsGameConfig} from "../BookOfShadowsGameConfig";
import {IconToggleButton} from "@nolimitcity/slot-launcher/bin/gui/buttons/IconToggleButton";
import {PointerStateIconSet} from "@nolimitcity/slot-launcher/bin/gui/buttons/states/sets/PointerStateIconSet";
import {Icon} from "@nolimitcity/slot-launcher/bin/gui/displayobjects/Icon";
import {PointerStateColorSet} from "@nolimitcity/slot-launcher/bin/gui/buttons/states/sets/PointerStateColorSet";
import {Orientation} from "@nolimitcity/slot-game/bin/core/stage/Orientation";
import {SpineTween} from "@nolimitcity/slot-game/bin/core/animation/SpineTween";
import {TimelineLite, TimelineMax, TweenLite} from "gsap";
import {Helper} from "@nolimitcity/slot-game/bin/core/utils/Helper";
import {BookOfShadows} from "../BookOfShadows";
import {BookOfShadowsGameModuleConfig} from "../BookOfShadowsGameModuleConfig";
import {SoundManager} from "../sound/SoundManager";
import {FontStyle} from "@nolimitcity/slot-launcher/bin/loader/font/FontStatics";
import {GameSetting} from "@nolimitcity/slot-game/bin/core/setting/GameSetting";
import {PointerState} from "@nolimitcity/slot-launcher/bin/gui/buttons/states/PointerState";

export class BookOfShadowsReelLockView extends BaseView {
    private _buttons: IconToggleButton[] = [];
    private _locks: PIXI.Sprite[] = [];
    private _lockCosts: PIXI.Text[] = [];
    private _layer: ScalableLayer;
    private _activeButtons: number = 0;
    private _activeLockAnimIndex: number = 0;
    private _lockAnims: PIXI.spine.Spine[] = [];
    private _lockAnimTween: SpineTween[] = [];

    private _lockAnimIdle: PIXI.spine.Spine;
    private _lockAnimTweenIdle: SpineTween;
    private _idleAnimActivated: boolean = false;

    private _reelLockAnimations: PIXI.spine.Spine[] = [];
    private _reelLockAnimTweens: SpineTween[] = [];
    private _isReplay: boolean = false;
    private _resourcesLoaded: boolean = false;
    private _initDataReceived: boolean = false;
    private _isRestore: boolean = false;
    private _nextReelSetName: string;

    private _bounceTLTutorial: TimelineMax;
    private _btnEnableTween: TweenLite;
    private _lockTextureTween: TweenLite;
    private _allowLockedReelsNextSpin: boolean = false;
    private _lockCostTl: TimelineLite;

    private _betPlaced: boolean = false;

    set betPlaced(value: boolean) {
        this._betPlaced = value;
    }

    /**
     * @description The game data.
     * @param allowLockedReelsNextSpin
     */
    public onGameData(allowLockedReelsNextSpin: boolean): void {
        this._allowLockedReelsNextSpin = allowLockedReelsNextSpin;
    }

    /**
     * @description To play bounce animation for tutorial.
     */
    public playBounceForGameTutorial(): void {
        this._bounceTLTutorial.restart();
    }

    /**
     * @description To stop bounce animation for tutorial.
     */
    public stopBounceForGameTutorial(): void {
        this._bounceTLTutorial.pause();
        this._bounceTLTutorial.kill();
        this._locks[1].scale.set(0.9);
    }

    /**
     * @description To play the lock idle animation triggered from event.
     * @param index
     */
    public playLockIdleAnimation(index: number): void {
        // if feature is not allowed
        if (!BookOfShadowsGameConfig.instance.LUCKY_LOCKS_ALLOWED_JURISDICTION) {
            return;
        }

        if (!this._idleAnimActivated || (this._lockAnimTweenIdle?.isActive())) {
            this._lockAnimTweenIdle?.kill();
            return;
        }

        this._lockAnimIdle.x = this._locks[index].x;
        this._lockAnimIdle.y = this._locks[index].y;
        this._lockAnimIdle.renderable = true;
        this._lockAnimTweenIdle = new SpineTween(this._lockAnimIdle, 0, "IdleAttention", -1);
        TweenLite.delayedCall(this._lockAnimTweenIdle.duration(), () => {
            this._lockAnimIdle.renderable = false;
        });
    }

    /**
     * @description To react when bet is placed.
     */
    public onBet(): void {
        // if feature is not allowed
        if (!BookOfShadowsGameConfig.instance.LUCKY_LOCKS_ALLOWED_JURISDICTION) {
            return;
        }

        this._lockAnimTweenIdle?.kill();

        this._idleAnimActivated = false;
        this.toggleButtonEnable(false);

        this._betPlaced = true;
    }

    /**
     * @description To react when the round is finished.
     */
    public onRoundFinished(): void {
        // if feature is not allowed
        if (!BookOfShadowsGameConfig.instance.LUCKY_LOCKS_ALLOWED_JURISDICTION) {
            return;
        }

        if (!this._allowLockedReelsNextSpin) {
            return;
        }
        this._idleAnimActivated = true;
        this.toggleButtonEnable(true);

        this._betPlaced = false;
    }

    /**
     * @description To stop all the reel lock animations when modes are changing.
     * @param show
     */
    public stopReelLockSpineAnimations(show: boolean): void {
        // if feature is not allowed
        if (show && !BookOfShadowsGameConfig.instance.LUCKY_LOCKS_ALLOWED_JURISDICTION) {
            return;
        }

        for (let i = 0; i < this._reelLockAnimations.length; i++) {
            this._reelLockAnimations[i].renderable = false;
            if (this._reelLockAnimTweens[i]) {
                this._reelLockAnimTweens[i].kill();
                this._reelLockAnimTweens[i].clear();
            }

            if (this._buttons[i].toggled) {
                this.adjustXForLocks(i, false);
            }

            // #99
            this._locks[i].texture = GameResources.getTextures("reelLockOpen")[0];
            this._buttons[i].toggled = false;

            // #104
            this._locks[i].visible = show;
            this._buttons[i].visible = show;
            this._lockCosts[i].visible = show;
            this._lockCosts[i].text = "";

            this._buttons[i].enable(this._allowLockedReelsNextSpin && BookOfShadowsGameConfig.instance.LUCKY_LOCKS_ALLOWED_VIA_BET);

            // #185
            this._lockAnims[i].renderable = false;
        }

        this._activeButtons = 0;
        BookOfShadowsGameConfig.instance.LOCKED_REEL = ["0", "0", "0", "0", "0"];
    }

    /**
     * @description To handle the replay round.
     */
    public handleReplay(): void {
        this._isReplay = true;
        this.processReplay();
    }

    /**
     * @description To display the reel lock costs against the lock buttons.
     * @param calledFromSpinStart
     */
    public displayLockCosts(calledFromSpinStart?: boolean): void {
        const lockedReel: string = BookOfShadows.calculateLockedReel(),
            rows: number = BookOfShadowsGameConfig.instance.ACTIVE_ROWS,
            details: any = BookOfShadowsGameModuleConfig.reelLockController.lockedReelDetails,
            maxWidth: number = 300;

        // if feature is not allowed
        if (!BookOfShadowsGameConfig.instance.LUCKY_LOCKS_ALLOWED_JURISDICTION) {
            return;
        }

        // #215
        if (this._lockTextureTween) {
            this._lockTextureTween.kill();
        }

        if (this._lockCostTl?.isActive()) {
            this._lockCostTl.pause().kill();
        }

        let texts: number[] = [];

        if (calledFromSpinStart) {
            for (let i: number = 0; i < this._lockCosts.length; i++) {
                this._lockCosts[i].text = "";
            }
            return;
        }

        if ((Object.keys(details).length === 0)) {
            return;
        }

        texts = details[rows + "_" + lockedReel].reelDisplayValues;
        this._lockCostTl = new TimelineLite();
        for (let i: number = 0; i < this._lockCosts.length; i++) {
            this._buttons[i].enable(true);

            if (texts[i] === 0 || texts[i] === null) {
                this._lockCosts[i].text = "";
                if (BookOfShadowsGameConfig.instance.LOCKED_REEL[i] === "0") {
                    this._buttons[i].enable(false);
                }
                continue;
            }
            this._lockCostTl.add(TweenLite.to(this._lockCosts[i].scale, 0.2, {
                x: 1.1,
                y: 1.1,
                onComplete: () => {
                    const text: string = texts[i].toFixed(2);
                    Helper.shrinkTextWidth(text, this._lockCosts[i], maxWidth);
                    this._lockCosts[i].text = text;
                    TweenLite.to(this._lockCosts[i].scale, 0.2, {
                        x: 1,
                        y: 1
                    });
                }
            }), 0);
        }
    }

    /**
     * @description To react on game's parsed init response
     * @param isRestore
     * @param nextReelSetName
     */
    public onGameInit(isRestore: boolean, nextReelSetName: string): void {
        this._isRestore = isRestore;
        this._initDataReceived = true;

        this._nextReelSetName = nextReelSetName;

        this.onGameReady();
    }

    /**
     * @description To toggle the state of reel lock buttons.
     * @param val
     */
    public toggleButtonEnable(val: boolean): void {
        // if feature is not allowed
        if (val && !BookOfShadowsGameConfig.instance.LUCKY_LOCKS_ALLOWED_JURISDICTION) {
            return;
        }

        for (let i: number = 0; i < this._buttons.length; ++i) {
            this._buttons[i].enable(val);
        }
    }

    /**
     * @description To react when resize happens.
     * @param resizeData
     */
    protected onResize(resizeData: IResizeData): void {
        let yPos: number;

        if (resizeData.orientation === Orientation.PORTRAIT) {
            yPos = 30;
        } else {
            yPos = BookOfShadowsGameConfig.instance.REEL_AREA_POS_Y + BookOfShadowsGameConfig.instance.REEL_AREA_HEIGHT + (0.2 * BookOfShadowsGameConfig.instance.SYMBOL_HEIGHT);
        }

        for (let i: number = 0; i < this._buttons.length; ++i) {
            this._buttons[i].y = yPos;
            this._locks[i].y = yPos;
            this._lockCosts[i].y = yPos;

            this._lockAnims[i].x = this._locks[i].x;
            this._lockAnims[i].y = this._locks[i].y;
        }

        this._lockAnimIdle.x = this._locks[this._activeLockAnimIndex].x;
        this._lockAnimIdle.y = this._locks[this._activeLockAnimIndex].y;

        super.onResize && super.onResize(resizeData);
    }

    /**
     * @inheritDoc
     */
    protected initAnimations(): void {
        this._layer = StageManager.getLayer(BookOfShadowsGameConfig.instance.LAYERS.REEL_LOCK_BUTTON.name);
        this._layer.addChild(this);

        this.createLocks();
        this.createLockSpines();
        this.createReelLockSpines();
        this.createLockCosts();

        this._resourcesLoaded = true;
        this.processReplay();
        this.createBounceForGameTutorial();

        this.onGameReady();
    }

    /**
     * @description To create the lock buttons.
     */
    private createLocks(): void {
        let lock: PIXI.Sprite;

        const numButtons: number = 5,
            texture: PIXI.Texture = GameResources.getTextures("reelLockButton")[0],
            downTexture: PIXI.Texture = GameResources.getTextures("reelLockButtonDown")[0],
            disabledTexture: PIXI.Texture = GameResources.getTextures("reelLockButtonDisabled")[0],
            reelPos: number[][] = BookOfShadowsGameConfig.instance.REEL_DISPLAY_POSITIONS,
            xOffset: number = 6;

        for (let i = 0; i < numButtons; i++) {
            let onIcons: PointerStateIconSet = new PointerStateIconSet(new Icon(downTexture)),
                offIcons: PointerStateIconSet = new PointerStateIconSet(new Icon(texture), undefined, undefined, new Icon(disabledTexture));

            let button: IconToggleButton = new IconToggleButton("" + i, onIcons, new PointerStateColorSet(0xffffffff), offIcons);
            button.addClickCallback(() => this.onButtonClick(button, i));
            button.toggled = false;
            button.name = "" + i;
            button.position.set(reelPos[i][0], 0);
            button.enable(false);
            button.pivot.set(button.width / 2, button.height / 2);

            lock = new PIXI.Sprite(GameResources.getTextures("reelLockOpen")[0]);
            lock.position.set((button.x - (button.width / 2) + (lock.width / 2)) - xOffset, 0);
            lock.anchor.set(0.5);
            lock.scale.set(0.9);

            if (i % 2 !== 0) {
                button.scale.x = -1;
            }

            this.addChild(button);
            this._buttons.push(button);
            this.addChild(lock);
            this._locks.push(lock);
        }
    }

    /**
     * @description To create the spines for lock buttons.
     */
    private createLockSpines(): void {
        let lockAnim: PIXI.spine.Spine,
            lockAnimIdle: PIXI.spine.Spine;

        const len: number = 5;

        for (let i = 0; i < len; i++) {
            lockAnim = new PIXI.spine.Spine(GameResources.getSpineAsset("lockAnim"));
            lockAnim.renderable = false;
            lockAnim.scale.set(0.9);
            this.addChild(lockAnim);
            this._lockAnims.push(lockAnim);
        }

        lockAnimIdle = new PIXI.spine.Spine(GameResources.getSpineAsset("lockAnim"));
        lockAnimIdle.renderable = false;
        lockAnimIdle.scale.set(0.9);
        this.addChild(lockAnimIdle);
        this._lockAnimIdle = lockAnimIdle;
    }

    /**
     * @description To create the reel lock spine animations to be played on the reels.
     */
    private createReelLockSpines(): void {
        const len: number = BookOfShadowsGameConfig.instance.REELS_NUM;
        let item: PIXI.spine.Spine;

        for (let i = 0; i < len; i++) {
            const pos: number[] = Helper.getSymbolPositions(i, 2);
            item = new PIXI.spine.Spine(GameResources.getSpineAsset("reelLock"));
            item.renderable = false;
            item.position.set(pos[0], pos[1]);

            this.addChild(item);
            this._reelLockAnimations.push(item);
        }
    }

    /**
     * @description To create the lock cost so that they will be on top.
     */
    private createLockCosts(): void {
        let lockCost: PIXI.Text;

        const numButtons: number = 5,
            reelPos: number[][] = BookOfShadowsGameConfig.instance.REEL_DISPLAY_POSITIONS,
            textStyle: PIXI.TextStyle = new PIXI.TextStyle({
                fill: "#CACACA",
                fontFamily: "Open Sans",
                fontSize: 20,
                fontStyle: FontStyle.NORMAL
            }),
            xOffset: number = 6;

        for (let i = 0; i < numButtons; i++) {
            lockCost = new PIXI.Text("", textStyle);
            lockCost.anchor.set(0.5);
            lockCost.position.set(reelPos[i][0] + this._locks[i].width / 4 - xOffset, 0);

            this.addChild(lockCost);
            this._lockCosts.push(lockCost);
        }
    }

    /**
     * @description To react on button click.
     * @param button
     * @param index
     */
    private onButtonClick(button: IconToggleButton, index: number): void {
        this._bounceTLTutorial.pause();
        this._bounceTLTutorial.kill();
        this._locks[1].scale.set(0.9);

        BookOfShadowsGameModuleConfig.gameTutorial.abort();

        if (button.toggled) {
            this._activeButtons--;
        } else {
            this._activeButtons++;
        }

        this._idleAnimActivated = (this._activeButtons === 0);

        if (this._activeButtons > 4) {
            this._activeButtons = 4;
            return;
        }

        if (this._btnEnableTween) {
            this._btnEnableTween.kill();
        }

        if (this._lockTextureTween) {
            this._lockTextureTween.kill();
        }

        this._lockAnimTweenIdle?.kill();
        this._lockAnimIdle.renderable = false;

        this._lockAnimTween[index]?.kill();
        this._lockAnims[index].renderable = false;

        this._activeLockAnimIndex = index;
        this._locks[index].visible = false;

        if (button.toggled) {
            this.adjustXForLocks(index, false);

            this.playLockLockAnimation(index, "reelLockOpen", "Unlock");
            this.playReelLockOpenAnimation(index);

            BookOfShadowsGameConfig.instance.LOCKED_REEL[index] = "0";

            SoundManager.playReelUnLock();
        } else {
            this.adjustXForLocks(index, true);

            this.playLockLockAnimation(index, "reelLock", "Lock");
            this.playReelLockAnimation(index);

            BookOfShadowsGameConfig.instance.LOCKED_REEL[index] = "1";

            SoundManager.playReelLock();
        }

        this.displayLockCosts();

        button.toggled = !button.toggled;

        BookOfShadowsGameModuleConfig.inactiveRowController.setBoostedBet();

        // #92 and #158
        for (let i: number = 0; i < this._buttons.length; i++) {
            this._buttons[i].interactive = false;
        }
        this._btnEnableTween = TweenLite.delayedCall(1, () => {
            if (!this._betPlaced) {
                // #158
                for (let i: number = 0; i < this._buttons.length; i++) {
                    if (this._buttons[i].pointerState === PointerState.IDLE) {
                        this._buttons[i].interactive = true;
                    }
                }
            }
        });
    }

    /**
     * @description To play the lock animations of the locks.
     * @param index
     * @param textureName
     * @param animationName
     */
    private playLockLockAnimation(index: number, textureName: string, animationName: string): void {
        this._lockAnims[index].x = this._locks[index].x;
        this._lockAnims[index].y = this._locks[index].y;
        this._lockAnims[index].renderable = true;
        this._lockAnimTween[index] = new SpineTween(this._lockAnims[index], 0, animationName, 0);
        this._lockTextureTween = TweenLite.delayedCall(this._lockAnimTween[index].duration(), () => {
            this._lockAnims[index].renderable = false;
            this._locks[index].texture = GameResources.getTextures(textureName)[0];
            this._locks[index].visible = true;
        });
    }

    /**
     * @description To play the reel lock animation on a particular reel.
     * @param id
     */
    private playReelLockAnimation(id: number): void {
        this._reelLockAnimations[id].renderable = true;
        this._reelLockAnimTweens[id] = new SpineTween(this._reelLockAnimations[id], 0, "ReelLock", 0);

        this._reelLockAnimations[id].alpha = 0;
        TweenLite.to(this._reelLockAnimations[id], 0.01, {alpha: 1});

        TweenLite.delayedCall(this._reelLockAnimTweens[id].duration(), () => {
            if (BookOfShadowsGameConfig.instance.LOCKED_REEL[id] === "1") {
                this._lockCosts[id].text = "";
            }

            this._reelLockAnimTweens[id] = new SpineTween(this._reelLockAnimations[id], 0, "ReelLockIdle", -1);
        });
    }

    /**
     * @description To play the reel unlock animation on a particular reel.
     * @param id
     */
    private playReelLockOpenAnimation(id: number): void {
        this._reelLockAnimTweens[id] = new SpineTween(this._reelLockAnimations[id], 0, "ReelUnlock", 0);

        TweenLite.delayedCall(this._reelLockAnimTweens[id].duration(), () => {
            this._reelLockAnimations[id].renderable = false;
            this._reelLockAnimTweens[id].kill();
            this._reelLockAnimTweens[id].clear();
        });
    }

    /**
     * @description To apply the reel lock graphics.
     */
    private processReplay(): void {
        if (this._isReplay && this._resourcesLoaded) {
            this.applyLockToReels();

            this._isReplay = false;
        }
    }

    /**
     * @description To create bounce animation for tutorial.
     */
    private createBounceForGameTutorial(): void {
        const bounceTl: TimelineMax = new TimelineMax({
                paused: true,
                repeat: 8,
                repeatDelay: 0
            }),
            duration: number = 0.3;

        bounceTl.to(this._locks[1].scale, duration, {
            x: 1.08,
            y: 1.08
        }).to(this._locks[1].scale, duration, {x: 0.9, y: 0.9});

        this._bounceTLTutorial = bounceTl;
    }

    /**
     * @description Method to react when resources are loaded and game's init response is parsed.
     */
    private onGameReady(): void {
        if (this._resourcesLoaded && this._initDataReceived) {
            if (this._isRestore) {
                if (this._nextReelSetName === "FREESPIN_REELSET") {
                    this.stopReelLockSpineAnimations(false);
                } else {
                    this.applyLockToReels();
                }
                this._isRestore = false;
            }

            if (!BookOfShadowsGameConfig.instance.LUCKY_LOCKS_ALLOWED_JURISDICTION) {
                this.stopReelLockSpineAnimations(false);
                this.toggleButtonEnable(false);

                return;
            }
        }
    }

    /**
     * @description To apply locks to the reels.
     */
    private applyLockToReels(): void {
        // if feature is not allowed
        if (!BookOfShadowsGameConfig.instance.LUCKY_LOCKS_ALLOWED_JURISDICTION) {
            return;
        }

        const lockedReel: string[] = BookOfShadowsGameConfig.instance.LOCKED_REEL;

        for (let i: number = 0; i < lockedReel.length; i++) {
            if (lockedReel[i] === "1") {
                this._buttons[i].toggled = true;
                this.adjustXForLocks(i, true);
                this.playLockLockAnimation(i, "reelLock", "Lock");
                this.playReelLockAnimation(i);
            }
        }
    }

    /**
     * @description To adjust the x position of locks.
     * @param i
     * @param add
     */
    private adjustXForLocks(i: number, add: boolean): void {
        const multiplyBy: number = (i % 2 === 0) ? 1 : -1,
            divideBy: number = (i % 2 === 0) ? 2 : 4,
            val: number = (this._buttons[i].width / divideBy - this._locks[i].width / divideBy) * multiplyBy;

        if (add) {
            this._locks[i].x += val;
        } else {
            this._locks[i].x -= val;
        }
    }
}